/**
 * What a refused draft save says, and where: each violation the server returned, with the key that
 * words its rule and the session and exercise its path points at.
 *
 * The server names a place by its position in the plan it was SENT (`phases[0].days[1].sessions[0]
 * .exercises[2]`, `PlanRequestPath`), so the place is found in that request and followed by slot
 * token into the document: the session by its letter on the card, the exercise by its name. A path
 * that names no session (the plan as a whole), or a position the request does not have, has no place.
 *
 * The rule's words are the builder's own (`saveErrorCopyKey`): the same refusal reads the same in
 * both. Two codes are Buddy's: the draft edit's own (a part of the plan missing, or a plan past
 * the 25 000 sets a draft holds), and a code this build does not know, which the builder words as
 * a connection problem and which is not one here.
 *
 * Pure: the violations, the request and the document in, keys and names out.
 */

import type { AiDraftViolation } from '~/entities/ai-conversation';
import { saveErrorCopyKey, UNKNOWN_SAVE_ERROR_KEY, type PlanDraft } from '~/entities/plan-draft';
import type { TrainWithMeModulesPlansDtosRequestsPostPlanTemplateRequest as PlanRequest } from '~/shared/api/generated/model';

import { sessionLetter } from './draftSessions';

export interface PlacedViolation {
  /** The i18n key of the rule that was broken. */
  readonly key: string;
  /** The phase's name, when the path names a session of a plan with more than one phase. */
  readonly phase: string | null;
  /** The session's letter on the card, when the path names one. */
  readonly session: string | null;
  /** The exercise's name, when the path names one. */
  readonly exercise: string | null;
}

/** The draft edit's own code: a part of the plan the save needs and the plan lacks, or a plan too large. */
const DRAFT_EDIT_CODE = 'ai_draft_edit_invalid';

const PATH = /^phases\[(\d+)\](?:\.days\[(\d+)\](?:\.sessions\[(\d+)\](?:\.exercises\[(\d+)\])?)?)?/;

function ruleKey(code: string, draft: PlanDraft): string {
  if (code === DRAFT_EDIT_CODE) return 'web:aiPlan.session.violation.draftRule';
  const key = saveErrorCopyKey(code, draft.mode);
  return key === UNKNOWN_SAVE_ERROR_KEY ? 'web:aiPlan.session.violation.unknown' : key;
}

function place(path: string, sent: PlanRequest | null, draft: PlanDraft): Pick<PlacedViolation, 'phase' | 'session' | 'exercise'> {
  const match = PATH.exec(path);
  const at = (group: string | undefined) => (group === undefined ? null : Number(group));
  const [phaseIndex, dayIndex, sessionIndex, exerciseIndex] = [at(match?.[1]), at(match?.[2]), at(match?.[3]), at(match?.[4])];
  const nowhere = { phase: null, session: null, exercise: null };
  if (sent === null || phaseIndex === null || dayIndex === null) return nowhere;
  const sentDay = sent.phases?.[phaseIndex]?.days?.[dayIndex];
  const phase = draft.phases.find((each) => each.days.some((day) => day.slotToken === sentDay?.slotToken));
  const index = phase?.days.findIndex((day) => day.slotToken === sentDay?.slotToken) ?? -1;
  if (phase === undefined || index < 0) return nowhere;
  const sentExercise =
    sessionIndex === null || exerciseIndex === null ? undefined : sentDay?.sessions?.[sessionIndex]?.exercises?.[exerciseIndex];
  const exercise = phase.days[index]?.exercises.find((each) => each.slotToken === sentExercise?.slotToken);
  return {
    // A letter names a session within its phase, so a plan of several phases says which.
    phase: draft.phases.length > 1 ? phase.name : null,
    session: sessionLetter(index),
    exercise: exercise?.name ?? null,
  };
}

export function placeViolations(
  violations: readonly AiDraftViolation[],
  sent: PlanRequest | null,
  draft: PlanDraft,
): PlacedViolation[] {
  return violations.map((violation) => ({ key: ruleKey(violation.code, draft), ...place(violation.path, sent, draft) }));
}
